import { useCallback, useEffect, useMemo, useReducer, useRef, type ReactNode } from 'react';
import { nextFish, serialiseFish, type Fish } from '../components/april';
import { AudioEngine } from '../audio/AudioEngine';
import { printConsoleEgg } from '../lib/console-egg';
import { S, formatClock, tList, type Locale } from '../i18n/strings';
import { randomInt } from '../lib/random';
import { writeString } from '../lib/storage';
import { ActionsContext, StateContext, type SteveActions } from './context';
import { initialState, reducer, type Action, type FxName } from './reducer';
import {
  APRIL_KEY,
  bumpSeenForToday,
  KIKOO_KEY,
  resolveFish,
  resolveKikoo,
  resolveLang,
  resolveSkin,
  resolveSkyblogTheme,
  rerollSkin,
  SB_THEME_KEY,
  stepSkin,
} from './resolve';
import { isSecretKey, matchSecret, SECRET_BUFFER } from './secrets';
import {
  PAD_COUNT,
  PAD_SLICE_MS,
  PAD_SLICES_MS,
  PAD_TIMES,
  type MixState,
  type PresetName,
  type Skin,
  type SkyblogTheme,
} from './types';

/** Durée d'affichage de chaque effet du bac (cf. handoff § Actions du bac). */
const FX_DURATION_MS: Record<FxName, number> = {
  feedFx: 2100,
  praiseFx: 2400,
  bubbleFx: 2900,
};

/** Rafraîchissement de l'horloge de la barre de menu. */
const CLOCK_INTERVAL_MS = 10_000;

interface Props {
  readonly children: ReactNode;
  /** Chemin du morceau, servi depuis la même origine que le site. */
  readonly audioSrc: string;
}

export function SteveProvider({ children, audioSrc }: Props): ReactNode {
  const [state, dispatch] = useReducer(
    reducer,
    null,
    () =>
      // Le compteur démarre à 0 : la lecture-écriture de `localStorage` est un
      // effet de bord, elle a lieu dans l'effet de montage ci-dessous.
      initialState(
        resolveSkin(window.location.search),
        resolveLang(window.location.search, navigator.language),
        0,
        resolveSkyblogTheme(),
        resolveKikoo(),
        resolveFish(),
      ),
  );

  // Déclaré avant les effets qui le consultent : l'écoute clavier a besoin du
  // skin courant sans se ré-abonner à chaque changement.
  const skinRef = useRef<Skin>(state.skin);
  skinRef.current = state.skin;

  // Même raison : le mot secret `poisson` doit savoir où sont les découpes déjà
  // collées sans que l'écoute clavier se ré-abonne à chaque poisson.
  const fishRef = useRef<readonly Fish[]>(state.fish);
  fishRef.current = state.fish;

  // Le moteur naît au premier geste : il lui faut l'état des curseurs à cet
  // instant, pas seulement leurs changements ultérieurs.
  const mixRef = useRef<MixState>(state.mix);
  mixRef.current = state.mix;

  const engineRef = useRef<AudioEngine | null>(null);
  const padTimeout = useRef<number | null>(null);
  const padInterval = useRef<number | null>(null);
  const seenBumped = useRef(false);

  // ── Moteur audio ────────────────────────────────────────────────────────
  // Créé au premier geste seulement : aucune requête, aucun AudioContext tant
  // que le visiteur n'a rien demandé.
  const getEngine = useCallback((): AudioEngine => {
    if (!engineRef.current) {
      engineRef.current = new AudioEngine(audioSrc, {
        onMissing: () => {
          dispatch({ type: 'audio-missing' });
        },
      });
      // Les curseurs peuvent avoir bougé avant le premier son : le moteur les
      // reçoit en naissant, et les reposera sur son graphe en le construisant.
      engineRef.current.applyMix(mixRef.current);
    }
    return engineRef.current;
  }, [audioSrc]);

  const clearPadTimers = useCallback((): void => {
    if (padTimeout.current !== null) {
      window.clearTimeout(padTimeout.current);
      padTimeout.current = null;
    }
    if (padInterval.current !== null) {
      window.clearInterval(padInterval.current);
      padInterval.current = null;
    }
  }, []);

  useEffect(
    () => () => {
      clearPadTimers();
      engineRef.current?.destroy();
      engineRef.current = null;
    },
    [clearPadTimers],
  );

  // Les curseurs du mixeur sont la source de vérité : le graphe suit l'état,
  // jamais l'inverse.
  useEffect(() => {
    engineRef.current?.applyMix(state.mix);
  }, [state.mix]);

  // ── Compteur du jour ────────────────────────────────────────────────────
  useEffect(() => {
    if (seenBumped.current) return;
    seenBumped.current = true;
    dispatch({ type: 'set-seen', seen: bumpSeenForToday() });
  }, []);

  // ── Horloge ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const tick = (): void => {
      dispatch({ type: 'tick', clock: formatClock(new Date(), state.lang) });
    };
    tick();
    const id = window.setInterval(tick, CLOCK_INTERVAL_MS);
    return () => {
      window.clearInterval(id);
    };
  }, [state.lang]);

  // ── Langue et skin sur <html> ───────────────────────────────────────────
  // La langue suit le navigateur et ne change plus en cours de visite : rien à
  // mémoriser, il suffit de la déclarer pour les lecteurs d'écran.
  useEffect(() => {
    document.documentElement.lang = state.lang;
  }, [state.lang]);

  // Steve dans la console. Une fois par chargement, dans la langue de la page :
  // le seul œuf qui n'a pas de mot secret, faute de pouvoir en deviner un dans
  // un panneau que le site ne contrôle pas.
  useEffect(() => {
    printConsoleEgg(state.lang);
  }, [state.lang]);

  useEffect(() => {
    document.documentElement.dataset['skin'] = state.skin;
  }, [state.skin]);

  // Le thème du skyblog survit au skin : on le mémorise dès qu'il change, même
  // si le visiteur repart aussitôt sur une autre interface.
  useEffect(() => {
    writeString(SB_THEME_KEY, state.sbTheme);
  }, [state.sbTheme]);

  // Le pseudo adopté au générateur, de même : il signe les messages sur les onze
  // interfaces, il doit survivre au tirage du skin suivant.
  useEffect(() => {
    writeString(KIKOO_KEY, state.kikoo);
  }, [state.kikoo]);

  // Les poissons d'avril aussi : on ne les laisse pas sur une interface, on les
  // laisse sur le site.
  useEffect(() => {
    writeString(APRIL_KEY, serialiseFish(state.fish));
  }, [state.fish]);

  // ── Effets du bac : extinction après leur durée ─────────────────────────
  useFxTimer(state.feedFx, 'feedFx', dispatch);
  useFxTimer(state.praiseFx, 'praiseFx', dispatch);
  useFxTimer(state.bubbleFx, 'bubbleFx', dispatch);

  // ── Mots secrets ────────────────────────────────────────────────────────
  //
  // Il n'y a aucun sélecteur d'interface à l'écran : on change de skin en
  // rechargeant la page (tirage au sort), en tapant `steve` pour en retirer une
  // autre au hasard, ou en tapant le nom de celle qu'on veut. La table et la
  // reconnaissance vivent dans `secrets.ts`, hors React : c'est là que se
  // trouvent les pièges, à commencer par les mots qui en masquent d'autres.
  useEffect(() => {
    let buffer = '';
    const onKeyDown = (event: KeyboardEvent): void => {
      const target = event.target;
      // Ne jamais espionner la frappe dans un champ de saisie.
      if (target instanceof HTMLElement) {
        const tag = target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable) return;
      }
      if (!isSecretKey(event.key)) return;

      buffer = (buffer + event.key.toLowerCase()).slice(-SECRET_BUFFER);
      const action = matchSecret(buffer);
      if (!action) return;
      buffer = '';

      if (action.kind === 'skin') dispatch({ type: 'set-skin', skin: action.skin });
      else if (action.kind === 'reroll') {
        dispatch({ type: 'set-skin', skin: rerollSkin(skinRef.current) });
      } else if (action.kind === 'egg') dispatch({ type: 'open-egg' });
      else {
        // Le placement a besoin du hasard et des découpes déjà posées : il se
        // calcule ici, le réducteur ne reçoit qu'une valeur.
        const spot = nextFish(fishRef.current);
        if (spot) dispatch({ type: 'stick-fish', spot });
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  // ── Actions ─────────────────────────────────────────────────────────────
  const playingRef = useRef(state.playing);
  playingRef.current = state.playing;
  const holdRef = useRef(state.hold);
  holdRef.current = state.hold;
  const langRef = useRef<Locale>(state.lang);
  langRef.current = state.lang;

  const actions = useMemo<SteveActions>(() => {
    /** Indice de variante pour une liste de journal, dans la langue courante. */
    const variantFor = (list: { fr: readonly string[]; en: readonly string[] }): number =>
      randomInt(tList(list, langRef.current).length);

    const stopPlayback = (): void => {
      clearPadTimers();
      engineRef.current?.pause();
      dispatch({ type: 'set-playing', playing: false });
    };

    return {
      bump: () => {
        dispatch({ type: 'bump-seen' });
      },
      cycleSkin: (step) => {
        const current = skinRef.current;
        dispatch({
          type: 'set-skin',
          skin: step === 0 ? rerollSkin(current) : stepSkin(current, step),
        });
      },
      setSkin: (skin) => {
        dispatch({ type: 'set-skin', skin });
      },
      setSkyblogTheme: (theme: SkyblogTheme) => {
        dispatch({ type: 'set-sb-theme', theme });
      },
      setKikoo: (name: string) => {
        dispatch({ type: 'set-kikoo', name });
      },
      peelFish: (id: number) => {
        dispatch({ type: 'peel-fish', id });
      },
      feed: () => {
        dispatch({ type: 'feed', variant: variantFor(S.log.feed) });
      },
      praise: () => {
        dispatch({ type: 'praise', variant: variantFor(S.log.praise) });
      },
      bubbles: () => {
        dispatch({ type: 'bubbles', count: 3 + randomInt(20) });
      },

      toggleAudio: () => {
        const engine = getEngine();
        clearPadTimers();
        if (playingRef.current) {
          engine.pause();
          dispatch({ type: 'set-playing', playing: false });
          dispatch({ type: 'log', key: 'song_stop' });
          return;
        }
        void engine.play().then((started) => {
          if (!started) return;
          dispatch({ type: 'set-playing', playing: true });
          dispatch({ type: 'log', key: 'song_start' });
        });
      },

      hitPad: (index) => {
        if (!Number.isInteger(index) || index < 0 || index >= PAD_COUNT) return;
        const engine = getEngine();
        const at = PAD_TIMES[index] ?? 0;
        // Chaque pad a sa propre longueur : « la-la-la » a besoin de quatre
        // secondes, « palala » d'une seconde et quart.
        const slice = PAD_SLICES_MS[index] ?? PAD_SLICE_MS;
        clearPadTimers();
        dispatch({ type: 'pad-hit', variant: variantFor(S.log.pad) });

        void engine.seekAndPlay(at).then((started) => {
          if (!started) return;
          dispatch({ type: 'set-playing', playing: true });
          if (holdRef.current) {
            // Bégaiement : on revient sans cesse au même point.
            padInterval.current = window.setInterval(() => {
              engine.seek(at);
            }, slice);
          } else {
            padTimeout.current = window.setTimeout(() => {
              engine.pause();
              dispatch({ type: 'set-playing', playing: false });
            }, slice);
          }
        });
      },

      stopPad: () => {
        stopPlayback();
        dispatch({ type: 'log', key: 'pad_stop' });
      },
      toggleFollower: () => {
        dispatch({ type: 'toggle-follower' });
      },
      toggleHold: () => {
        clearPadTimers();
        dispatch({ type: 'toggle-hold' });
      },
      setMix: (patch: Partial<MixState>) => {
        dispatch({ type: 'set-mix', patch });
      },
      preset: (name: PresetName) => {
        dispatch({ type: 'preset', name });
      },
      toggleWobble: () => {
        dispatch({ type: 'toggle-wobble' });
      },
      setDraft: (draft) => {
        dispatch({ type: 'set-draft', draft });
      },
      postEntry: () => {
        dispatch({ type: 'post-entry', at: Date.now() });
      },
      openEgg: () => {
        dispatch({ type: 'open-egg' });
      },
      closeEgg: () => {
        dispatch({ type: 'close-egg' });
      },
      readBars: (out) => engineRef.current?.readBars(out) ?? false,
    };
  }, [clearPadTimers, getEngine]);

  return (
    <StateContext.Provider value={state}>
      <ActionsContext.Provider value={actions}>{children}</ActionsContext.Provider>
    </StateContext.Provider>
  );
}

/** Éteint un effet du bac une fois sa durée écoulée, sans écraser le suivant. */
function useFxTimer(token: number, fx: FxName, dispatch: (action: Action) => void): void {
  useEffect(() => {
    if (token === 0) return;
    const id = window.setTimeout(() => {
      dispatch({ type: 'fx-end', fx, token });
    }, FX_DURATION_MS[fx]);
    return () => {
      window.clearTimeout(id);
    };
  }, [token, fx, dispatch]);
}
